import cv from "@techstark/opencv-js";
import Line from "./Line"

export enum ZoneNames {
  T1 = 'T1',
  T25 = 'T25',
  M1 = 'M1',
  M2 = 'M2',
  M3 = 'M3',
  M4 = 'M4',
  M5 = 'M5',
  MF = 'MF',
  HM = 'HM',
  HL = 'HL',
}

export const ZoneColors = [
  '#007e2eff',
  '#f59e0b',
  '#f5260bff',
  '#49bb73ff',
  '#641515ff',
  '#180680ff',
  '#60a5fa',
  '#ffee00ff',
  '#ec6cd7ff',
  '#b0f85eff',
];

type ROI = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export default class zoneProcessor {
  zoneSensorPositions: cv.Point[][] = [];
  controlPoints: cv.Point[] = [];

  AddPointF(p1: cv.Point, p2: cv.Point): cv.Point {
    return new cv.Point(p1.x + p2.x, p1.y + p2.y);
  }

  MinusPointF(p1: cv.Point, p2: cv.Point): cv.Point {
    return new cv.Point(p1.x - p2.x, p1.y - p2.y);
  }

  PointbyPercent(axis: { start: cv.Point, end: cv.Point }, percent: number): cv.Point {
    const dist = Math.sqrt((axis.start.x - axis.end.x) * (axis.start.x - axis.end.x) + (axis.start.y - axis.end.y) * (axis.start.y - axis.end.y));
    const pdist = dist * percent;

    const lineDir = new Line();
    lineDir.calculateDirection(axis);
    const axisDirection = lineDir.direction;

    const p = new cv.Point((axisDirection.x * pdist + axis.start.x), (axisDirection.y * pdist + axis.start.y));

    return p;
  }

  BringPointFtoROI(p: cv.Point, ROI: ROI): cv.Point {
    let x = p.x;
    let y = p.y;

    if (x < ROI.x) x = ROI.x;
    if (x > ROI.x + ROI.width) x = ROI.x + ROI.width;
    if (y < ROI.y) y = ROI.y;
    if (y > ROI.y + ROI.height) y = ROI.y + ROI.height;

    return new cv.Point(x, y);
  }

  LineIntersectionPoint(
    ps1: cv.Point,
    pe1: cv.Point,
    ps2: cv.Point,
    pe2: cv.Point
  ): cv.Point2f | null {
    const A1 = pe1.y - ps1.y;
    const B1 = ps1.x - pe1.x;
    const C1 = A1 * ps1.x + B1 * ps1.y;

    const A2 = pe2.y - ps2.y;
    const B2 = ps2.x - pe2.x;
    const C2 = A2 * ps2.x + B2 * ps2.y;

    const delta = A1 * B2 - A2 * B1;
    if (delta === 0) {
      return ps1;
    }

    const intersection = new cv.Point(((B2 * C1 - B1 * C2) / delta), ((A1 * C2 - A2 * C1) / delta))

    return intersection;
  }

  autoMatch(ROI: ROI, axis: { start: cv.Point, end: cv.Point }, widthLine: { start: cv.Point, end: cv.Point }, heelLine: { start: cv.Point, end: cv.Point }, isRightFoot: boolean) {
    if (isRightFoot) {
      const temp = widthLine.end;
      widthLine.end = widthLine.start;
      widthLine.start = temp;

      const temp2 = heelLine.end;
      heelLine.end = heelLine.start;
      heelLine.start = temp2;
    }


    const metatarsalMidPointPercent = 0.3;
    const metatarsalUpPointPercent = 0.15;
    const metatarsalDownPointPercent = 0.4;
    const heelUpPointPercent = 0.7;
    const heelMidPointPercent = 0.85;

    const helper1 = this.LineIntersectionPoint(axis.start, axis.end, widthLine.start, widthLine.end);
    if (!helper1) throw new Error("Lines are parallel, no intersection.");


    const dirToeH = this.MinusPointF(widthLine.end, helper1);
    const toe1 = this.PointbyPercent(axis, metatarsalUpPointPercent);
    const toeD1 = this.PointbyPercent(axis, metatarsalDownPointPercent);
    const heel1 = this.PointbyPercent(axis, heelUpPointPercent);
    const dirToeV = this.MinusPointF(toe1, helper1);
    const dirMet = this.MinusPointF(widthLine.start, helper1);

    const dirToeUD = this.MinusPointF(toeD1, toe1);

    const metatarsalUp = { start: toe1, end: widthLine.start };
    const toe0 = this.AddPointF(widthLine.end, dirToeV);
    const met1 = this.PointbyPercent(metatarsalUp, 0.25);
    const met2 = this.PointbyPercent(metatarsalUp, 0.5);
    const met3 = this.PointbyPercent(metatarsalUp, 0.75);


    this.controlPoints = [];

    const lineDir = new Line();
    lineDir.calculateDirection(axis);
    const axisDirection = lineDir.direction;

    lineDir.calculateDirection(widthLine);
    const widthDirection = lineDir.direction;

    this.controlPoints.push(this.MinusPointF(this.AddPointF(this.AddPointF(axis.start, dirToeH), axisDirection), widthDirection));
    this.controlPoints.push(axis.start);
    this.controlPoints.push(this.AddPointF(this.AddPointF(this.AddPointF(axis.start, dirMet), this.AddPointF(axisDirection, axisDirection)), widthDirection));

    this.controlPoints.push(toe0);
    this.controlPoints.push(toe1);
    this.controlPoints.push(met1);
    this.controlPoints.push(met2);
    this.controlPoints.push(met3);
    this.controlPoints.push(widthLine.start)

    const toe0D = this.AddPointF(toe0, dirToeUD);
    const met4D = this.AddPointF(widthLine.start, dirToeUD);

    this.controlPoints.push(toe0D);
    this.controlPoints.push(this.AddPointF(toe1, dirToeUD));
    this.controlPoints.push(this.AddPointF(met1, dirToeUD));
    this.controlPoints.push(this.AddPointF(met2, dirToeUD));
    this.controlPoints.push(this.AddPointF(met3, dirToeUD));
    this.controlPoints.push(met4D);



    const helper2 = this.LineIntersectionPoint(axis.start, axis.end, heelLine.start, heelLine.end);
    if (!helper2) throw new Error("Lines are parallel, no intersection.");

    const dirHeelRight = this.MinusPointF(heelLine.end, helper2);
    const dirHeelLeft = this.MinusPointF(heelLine.start, helper2);
    const heelDownRight = this.AddPointF(axis.end, dirHeelRight);
    const heelDownMid = this.AddPointF(axis.end, axisDirection);
    const heelDownLeft = this.AddPointF(axis.end, dirHeelLeft);

    const heelUpRight = this.PointbyPercent({ start: heelDownRight, end: toe0D }, 0.5);
    const heelUpLeft = this.PointbyPercent({ start: heelDownLeft, end: met4D }, 0.5);

    this.controlPoints.push(heelUpRight);
    this.controlPoints.push(heel1);
    this.controlPoints.push(heelUpLeft);

    this.controlPoints.push(heelDownRight);
    this.controlPoints.push(heelDownMid);
    this.controlPoints.push(heelDownLeft);

    for (let i = 0; i < this.controlPoints.length; i++) {
      this.controlPoints[i] = this.BringPointFtoROI(this.controlPoints[i], ROI);
    }

    this.computeZoneSensors(ROI);
  }

  autoMatchByControlPoints(ROI: ROI, cPoints: cv.Point[], widthLine: { start: cv.Point, end: cv.Point }, heelLine: { start: cv.Point, end: cv.Point }, isRightFoot: boolean) {
    if (isRightFoot) {
      const temp = widthLine.end;
      widthLine.end = widthLine.start;
      widthLine.start = temp;

      const temp2 = heelLine.end;
      heelLine.end = heelLine.start;
      heelLine.start = temp2;
    }

    this.controlPoints = [];

    for (var i = 0; i < cPoints.length - 2; i++) {
      this.controlPoints.push(cPoints[i]);
    }

    for (var i = 0; i < this.controlPoints.length; i++) {
      this.controlPoints[i] = this.BringPointFtoROI(this.controlPoints[i], ROI);
    }

    this.computeZoneSensors(ROI);
  }

  GetZonePolygon(zname: ZoneNames): cv.Point[] | null {

    if (this.controlPoints != null) {
      if (zname == ZoneNames.T1) {
        return [this.controlPoints[0], this.controlPoints[1], this.controlPoints[4], this.controlPoints[3]];
      }
      else if (zname == ZoneNames.T25) {
        return [this.controlPoints[1], this.controlPoints[2], this.controlPoints[8], this.controlPoints[7], this.controlPoints[6], this.controlPoints[5], this.controlPoints[4]];
      }
      else if (zname == ZoneNames.M1) {
        return [this.controlPoints[3], this.controlPoints[4], this.controlPoints[10], this.controlPoints[9]];
      }
      else if (zname == ZoneNames.M2) {
        return [this.controlPoints[4], this.controlPoints[5], this.controlPoints[11], this.controlPoints[10]];
      }
      else if (zname == ZoneNames.M3) {
        return [this.controlPoints[5], this.controlPoints[6], this.controlPoints[12], this.controlPoints[11]];
      }
      else if (zname == ZoneNames.M4) {
        return [this.controlPoints[6], this.controlPoints[7], this.controlPoints[13], this.controlPoints[12]];
      }
      else if (zname == ZoneNames.M5) {
        return [this.controlPoints[7], this.controlPoints[8], this.controlPoints[14], this.controlPoints[13]];
      }
      else if (zname == ZoneNames.MF) {
        return [this.controlPoints[9], this.controlPoints[10], this.controlPoints[11], this.controlPoints[12], this.controlPoints[13], this.controlPoints[14], this.controlPoints[17], this.controlPoints[16], this.controlPoints[15]];
      }
      else if (zname == ZoneNames.HM) {
        return [this.controlPoints[15], this.controlPoints[16], this.controlPoints[19], this.controlPoints[18]];
      }
      else if (zname == ZoneNames.HL) {
        return [this.controlPoints[16], this.controlPoints[17], this.controlPoints[20], this.controlPoints[19]];
      }
      else {
        return null;
      }
    }
    else {
      return null;
    }
  }

  IsPointInPolygon(point: cv.Point, polygon: cv.Point[]): boolean {
    const length = polygon.length;
    let i = 0;
    let inside = false;

    const pointX = point.x;
    const pointY = point.y;

    let startX: number, startY: number, endX: number, endY: number;

    let endPoint = polygon[length - 1];
    endX = endPoint.x;
    endY = endPoint.y;

    while (i < length) {
      startX = endX;
      startY = endY;

      endPoint = polygon[i++];
      endX = endPoint.x;
      endY = endPoint.y;

      const intersect = (endY > pointY) !== (startY > pointY) &&
        (pointX - endX) < ((pointY - endY) * (startX - endX)) / (startY - endY);

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  }

  GetZoneSensors(zname: ZoneNames, ROI: ROI): cv.Point[] {
    const polygon = this.GetZonePolygon(zname);

    const insidePoints: cv.Point[] = [];
    if (!polygon) return insidePoints;

    const left = ROI.x;
    const top = ROI.y;
    const right = ROI.x + ROI.width;
    const bottom = ROI.y + ROI.height;

    for (let x = left; x <= right; x++) {
      for (let y = top; y <= bottom; y++) {
        const p = new cv.Point(x, y);

        if (this.IsPointInPolygon(p, polygon)) {
          insidePoints.push(p);
        }
      }
    }

    return insidePoints;
  }

  computeZoneSensors(ROI: ROI) {
    const zoneTypes = Object.values(ZoneNames);
    for (const z of zoneTypes) {
      const zPoints = this.GetZoneSensors(z, ROI);
      this.zoneSensorPositions[z as unknown as number] = zPoints;
    }
  }

  GetForeFootZoneSensors(ROI: cv.Rect) {
    let insidepoints: cv.Point[] = [];

    insidepoints.push(...this.GetZoneSensors(ZoneNames.M1, ROI));
    insidepoints.push(...this.GetZoneSensors(ZoneNames.M2, ROI));
    insidepoints.push(...this.GetZoneSensors(ZoneNames.M3, ROI));
    insidepoints.push(...this.GetZoneSensors(ZoneNames.M4, ROI));
    insidepoints.push(...this.GetZoneSensors(ZoneNames.M5, ROI));

    return insidepoints;
  }

  GetRearFootZoneSensors(ROI: cv.Rect) {
    let insidepoints: cv.Point[] = [];

    insidepoints.push(...this.GetZoneSensors(ZoneNames.HM, ROI));
    insidepoints.push(...this.GetZoneSensors(ZoneNames.HL, ROI));

    return insidepoints;
  }

  GetMidFootZoneSensors(ROI: cv.Rect) {
    return this.GetZoneSensors(ZoneNames.MF, ROI);
  }

  GetZoneCenteroid(zname: ZoneNames) {
    const PolyPoints = this.GetZonePolygon(zname);
    return this.FindCentroid(PolyPoints);
  }

  FindCentroid(Points: cv.Point[]) {
    if (Points.length == 1) return Points[0];

    // Add the first point at the end of the array.
    var num_points = Points.length;
    const pts: cv.Point[] = [...Points];

    pts[num_points] = Points[0];

    // Find the centroid.
    var X = 0;
    var Y = 0;
    var second_factor;
    for (let i = 0; i < num_points; i++) {
      second_factor =
        pts[i].x * pts[i + 1].y -
        pts[i + 1].x * pts[i].y;
      X += (pts[i].x + pts[i + 1].x) * second_factor;
      Y += (pts[i].y + pts[i + 1].y) * second_factor;
    }

    // Divide by 6 times the polygon's area.
    var polygon_area = this.PolygonArea(Points);
    X /= (6 * polygon_area);
    Y /= (6 * polygon_area);

    // If the values are negative, the polygon is
    // oriented counterclockwise so reverse the signs.
    if (X < 0) {
      X = -X;
      Y = -Y;
    }

    return new cv.Point(X, Y);
  }

  PolygonArea(Points: cv.Point[]) {
    const num_points = Points.length;
    var pts = [...Points];
    pts[num_points] = Points[0];

    // Get the areas.
    var area = 0;
    for (var i = 0; i < num_points; i++) {
      area +=
        (pts[i + 1].x - pts[i].x) *
        (pts[i + 1].y + pts[i].y) / 2;
    }

    return Math.abs(area);
  }

  static cloneFrom(original: zoneProcessor): zoneProcessor {
    const copy = new zoneProcessor();

    copy.zoneSensorPositions = original.zoneSensorPositions.map(arr =>
      arr ? arr.map(p => new cv.Point(p.x, p.y)) : []
    );

    copy.controlPoints = original.controlPoints.map(p => new cv.Point(p.x, p.y));

    return copy;
  }

  GetFillStyle(zoneName: ZoneNames, alpha: number): string {
    const baseColor = ZoneColors[Object.values(ZoneNames).indexOf(zoneName)];
    // Remove '#' and handle possible 'ff' alpha at the end
    const hex = baseColor.replace('#', '').substring(0, 6);
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 255})`;
  }

}
